/**
 * Phase 9.7.22 — 🎧 Admin Support Tickets dashboard
 * Route: /admin/support (admin only)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STATUS_META = {
  open:      { label: "Open",      cls: "bg-amber-500/15 text-amber-200 border-amber-400/40" },
  reviewing: { label: "Reviewing", cls: "bg-sky-500/15 text-sky-200 border-sky-400/40" },
  fixed:     { label: "Fixed",     cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" },
  closed:    { label: "Closed",    cls: "bg-gray-700/60 text-gray-200 border-gray-500/40" },
};

const ISSUE_LABEL = {
  bug: "🐞 Bug",
  login: "🔐 Login",
  upload: "🎵 Upload",
  gifting: "🎁 Gifting",
  payouts: "💸 Payouts",
  feature_suggestion: "💡 Feature",
  beta_feedback: "🧪 Beta",
  other: "❓ Other",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const TicketCard = ({ t, onUpdate }) => {
  const [reply, setReply] = useState(t.admin_reply || "");
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[t.status] || STATUS_META.open;

  const setStatus = async (newStatus) => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/support/tickets/${t.ticket_id}`, {
        status: newStatus,
      });
      onUpdate(r.data.ticket);
      toast.success(`Marked ${newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveReply = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/support/tickets/${t.ticket_id}`, {
        admin_reply: reply,
      });
      onUpdate(r.data.ticket);
      toast.success("Reply note saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="p-4 rounded-xl border border-amber-400/20 bg-gradient-to-br from-purple-900/10 to-black"
      data-testid={`admin-ticket-${t.ticket_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold text-sm">{t.name}</span>
            <span className="text-[10px] text-amber-300/70 font-mono">{t.ticket_id}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${meta.cls}`} data-testid={`admin-ticket-status-${t.ticket_id}`}>
              {meta.label}
            </span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-purple-500/15 border border-purple-400/40 text-purple-200">
              {ISSUE_LABEL[t.issue_type] || t.issue_type}
            </span>
          </div>
          <p className="text-[12px] text-amber-200/70 mt-1">
            {t.email}
            {t.user_id && <> · <span className="font-mono">{t.user_id}</span></>}
            {" · "}
            {formatDate(t.created_at)}
          </p>
          <p className="text-sm text-white mt-3 whitespace-pre-line">{t.message}</p>
          {t.source_url && (
            <p className="mt-2 text-[10px] text-amber-300/70 truncate">
              from: <a href={t.source_url} target="_blank" rel="noreferrer" className="underline">{t.source_url}</a>
            </p>
          )}
          {t.screenshot_url && (
            <a href={t.screenshot_url} target="_blank" rel="noreferrer" className="mt-3 inline-block" data-testid={`admin-ticket-screenshot-${t.ticket_id}`}>
              <img src={t.screenshot_url} alt="screenshot" className="max-h-48 rounded border border-amber-400/30 hover:border-amber-400/70" />
            </a>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {["open", "reviewing", "fixed", "closed"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant="outline"
            disabled={busy || t.status === s}
            onClick={() => setStatus(s)}
            className={`text-xs ${t.status === s ? "bg-amber-500/20 border-amber-400/60 text-amber-100" : "border-amber-400/30 text-amber-200"}`}
            data-testid={`admin-ticket-set-${s}-${t.ticket_id}`}
          >
            {STATUS_META[s].label}
          </Button>
        ))}
      </div>

      <div className="mt-3">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={2}
          placeholder="Internal reply note (visible to admins)"
          className="bg-black/40 border-amber-400/30 text-white text-sm"
          data-testid={`admin-ticket-reply-input-${t.ticket_id}`}
        />
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            disabled={busy || reply === (t.admin_reply || "")}
            onClick={saveReply}
            className="bg-gradient-to-r from-amber-500 to-pink-500 text-white font-bold"
            data-testid={`admin-ticket-save-reply-${t.ticket_id}`}
          >
            💾 Save note
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminSupportPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const [authChecked, setAuthChecked] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [issueFilter, setIssueFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Phase 9.7.22 fix — guard route on the frontend so non-admins are
  // redirected instead of seeing the admin chrome with an inline error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        const role = r.data?.role;
        const badges = r.data?.badges || [];
        const isAdmin =
          role === "admin" ||
          role === "moderator" ||
          badges.includes("admin") ||
          badges.includes("founder");
        if (!isAdmin) {
          toast.error("Admin access required");
          navigate("/dashboard", { replace: true });
          return;
        }
        if (!cancelled) setAuthChecked(true);
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (filter) qs.set("status", filter);
      if (issueFilter) qs.set("issue_type", issueFilter);
      qs.set("limit", "100");
      const r = await api.get(`/admin/support/tickets?${qs.toString()}`);
      setTickets(r.data?.tickets || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load tickets");
    } finally {
      setLoading(false);
    }
  }, [filter, issueFilter]);

  useEffect(() => {
    if (authChecked) load();
  }, [load, authChecked]);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation currentUser={currentUser} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-6" data-testid="admin-support-auth-gate">
          <p className="text-sm text-amber-200/60">Checking admin access…</p>
        </div>
      </div>
    );
  }

  const handleUpdate = (updated) => {
    setTickets((prev) => prev.map((t) => (t.ticket_id === updated.ticket_id ? updated : t)));
    load();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SharedNavigation currentUser={currentUser} />

      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-6">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[3px] font-black text-amber-200">
              🎧 Admin · Support
            </p>
            <h1 className="text-3xl sm:text-4xl font-black text-white mt-1">Support tickets</h1>
          </div>
          <Link to="/admin" className="text-xs text-amber-300/80 hover:text-amber-200 underline">
            ← back to admin
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 space-y-6">
        {/* Status filters */}
        <div className="flex flex-wrap gap-2" data-testid="admin-support-filters">
          <button
            onClick={() => setFilter("")}
            className={`text-xs px-3 py-1.5 rounded-full border font-bold ${filter === "" ? "bg-amber-500/20 border-amber-400/60 text-amber-100" : "bg-black/40 border-amber-400/30 text-amber-300"}`}
            data-testid="admin-filter-status-all"
          >
            All ({Object.values(counts).reduce((a, b) => a + (b || 0), 0)})
          </button>
          {["open", "reviewing", "fixed", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border font-bold ${filter === s ? "bg-amber-500/20 border-amber-400/60 text-amber-100" : "bg-black/40 border-amber-400/30 text-amber-300"}`}
              data-testid={`admin-filter-status-${s}`}
            >
              {STATUS_META[s].label} ({counts[s] || 0})
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-amber-200/60">Loading…</p>}
        {error && !loading && (
          <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200" data-testid="admin-support-error">
            {error}
          </div>
        )}

        {!loading && !error && tickets.length === 0 && (
          <div className="p-8 rounded-xl border border-amber-400/20 bg-gradient-to-br from-amber-900/10 to-black text-center" data-testid="admin-support-empty">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-white font-bold">No tickets match this filter.</p>
          </div>
        )}

        {!loading && !error && tickets.length > 0 && (
          <div className="space-y-3" data-testid="admin-support-tickets">
            {tickets.map((t) => (
              <TicketCard key={t.ticket_id} t={t} onUpdate={handleUpdate} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSupportPage;
